import type { Exercise } from '../../types.ts';
export const batch6Reading: Exercise[] = [
  {
    id: 'reading-peigneur',
    category: 'reading',
    title: 'Lake Peigneur',
    topic: 'Lake Peigneur',
    difficulty: 'Challenging',
    article: {
      title: 'Lake Peigneur',
      url: 'https://en.wikipedia.org/wiki/Lake_Peigneur',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'An oil-drilling accident connected a shallow Louisiana lake to a salt mine beneath it. The article follows a rapidly enlarging breach, a reversed canal and the limits of the later investigation.',
      scope: {
        wordCount: 857,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Lake_Peigneur',
            wordCount: 99,
            anchors: [''],
          },
          {
            title: 'Drilling disaster',
            url: 'https://en.wikipedia.org/wiki/Lake_Peigneur#Drilling_disaster',
            wordCount: 758,
            anchors: ['Drilling_disaster', 'Cause', 'Aftermath'],
          },
        ],
      },
    },
    reading: {
      title: 'Lake Peigneur — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'breach',
          title: 'A small opening can grow through feedback',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The drill punctured the mine beneath the lake, allowing fresh water to enter underground spaces. Water dissolved salt, enlarging the opening and weakening salt pillars supporting mine levels. A larger opening allowed more water to enter. This coupled flow and dissolution helps explain why the event grew far beyond the original drill diameter.',
            },
            {
              kind: 'paragraph',
              text: 'The mine already contained extensive excavated voids, so the water had somewhere to go. Describing the event as a surface whirlpool alone misses the connected underground volume and the material being dissolved. The cause involved the interaction of drilling, mine geometry, water flow and salt rather than one isolated visible effect.',
            },
          ],
        },
        {
          id: 'reverse',
          title: 'Flow direction responds to the new level difference',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The canal normally carried water from the lake toward the bay. As the lake drained into the mine, the relative water levels changed and the canal flow reversed. Water from the bay then moved into the depleted lakebed. The same channel can carry flow in a different direction when its hydraulic conditions change.',
            },
            {
              kind: 'paragraph',
              text: 'The reversal brought salty water into a previously freshwater system and produced a temporary waterfall as water entered the deepened area. The direction was not fixed by the canal’s usual function. A useful causal sequence is breach, falling lake level, reversed flow, then refilling with water from the bay.',
            },
          ],
        },
        {
          id: 'air',
          title: 'Incoming water displaced another fluid',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The underground caverns were not an empty mathematical volume: they contained air. As water entered, it displaced and compressed that air. The article describes air erupting through mine shafts, followed later by geysers. Tracking both water and air explains why effects appeared at shafts away from the original opening.',
            },
            {
              kind: 'paragraph',
              text: 'This does not require a volcanic source or steam generation. A volume balance is enough to identify the basic mechanism: material entering a confined connected space changes the pressure and pushes existing material toward escape routes. The exact dynamics were complex, but the article directly supplies the displacement account.',
            },
          ],
        },
        {
          id: 'escape',
          title: 'A disaster’s physical scale does not determine its human toll',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article reports that all fifty-five employees in the mine escaped after an electrician noticed incoming water and sounded an alarm. Training and orderly use of the available elevator contributed to the evacuation. The absence of human deaths should therefore not be read as evidence that the event was small or harmless.',
            },
            {
              kind: 'paragraph',
              text: 'The drilling crew and a fisherman also escaped. The question focuses on the operational explanation given for the miners’ survival, rather than asking the reader to memorize a head count. Detection, warning and coordinated evacuation are distinct steps in the account.',
            },
          ],
        },
        {
          id: 'evidence',
          title: 'Distinguish the mechanism from assigning blame',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The broad connection between drilling and the flooded salt mine is described, but evidence needed to settle responsibility was lost in the event. The investigation could not determine whether drilling was in the wrong place or whether the mine maps were inaccurate. Knowing a physical sequence does not automatically identify which prior action or record was at fault.',
            },
            {
              kind: 'paragraph',
              text: 'The later settlements do not supply a substitute technical finding about that unresolved question. The explanation preserves the article’s stated uncertainty and avoids importing a precise mistaken-coordinate story. A source can support a causal mechanism while leaving the detailed allocation of responsibility unsettled.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Lake Peigneur · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Lake_Peigneur',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b6-peigneur-1',
        prompt: 'Why could the initial drill opening grow into a much larger failure?',
        options: [
          {
            id: '0',
            text: 'The lake’s weight fractured intact rock before any mine opening was breached.',
          },
          {
            id: '1',
            text: 'Salt dissolved only at the surface, leaving the mine supports intact.',
          },
          {
            id: '2',
            text: 'Fresh water dissolved salt and weakened supporting structures as it entered.',
          },
          {
            id: '3',
            text: 'Air pressure from the mine pushed water outward and excavated the lake from below.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Drilling disaster and Cause describe dissolution enlarging the breach and weakening salt pillars.',
        sectionIds: ['breach'],
        evidence:
          'Drilling disaster and Cause describe dissolution enlarging the breach and weakening salt pillars. Verified in assigned source section: Cause.',
        sourceSection: {
          title: 'Wikipedia: Cause',
          url: 'https://en.wikipedia.org/wiki/Lake_Peigneur#Cause',
        },
      },
      {
        id: 'b6-peigneur-2',
        prompt: 'What caused the canal’s usual flow to reverse?',
        options: [
          {
            id: '0',
            text: 'The lake’s drainage changed the relative water levels, drawing bay water inward.',
          },
          {
            id: '1',
            text: 'A storm surge raised the bay before the mine was breached.',
          },
          {
            id: '2',
            text: 'The initial freshwater inflow increased the lake level above the bay.',
          },
          {
            id: '3',
            text: 'Salt dissolving in the lake caused denser water to flow uphill through the canal.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Drilling disaster connects lake drainage with reversed flow from Vermilion Bay.',
        sectionIds: ['reverse'],
        evidence:
          'Drilling disaster connects lake drainage with reversed flow from Vermilion Bay. Verified in assigned source section: Drilling_disaster.',
        sourceSection: {
          title: 'Wikipedia: Drilling disaster',
          url: 'https://en.wikipedia.org/wiki/Lake_Peigneur#Drilling_disaster',
        },
      },
      {
        id: 'b6-peigneur-3',
        prompt: 'What explains the eruptions of air through mine shafts?',
        options: [
          {
            id: '0',
            text: 'Steam formed when lake water contacted drilling machinery.',
          },
          {
            id: '1',
            text: 'Natural gas released from a petroleum reservoir ignited below the lake.',
          },
          {
            id: '2',
            text: 'Air was drawn down the shafts as a vacuum developed above the entering water.',
          },
          {
            id: '3',
            text: 'Water displaced and compressed air already in the caverns',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The article explicitly describes air displaced by incoming water escaping through shafts.',
        sectionIds: ['air'],
        evidence:
          'The article explicitly describes air displaced by incoming water escaping through shafts. Verified in assigned source section: Drilling_disaster.',
        sourceSection: {
          title: 'Wikipedia: Drilling disaster',
          url: 'https://en.wikipedia.org/wiki/Lake_Peigneur#Drilling_disaster',
        },
      },
      {
        id: 'b6-peigneur-4',
        prompt: 'Which factors does the account credit with the miners’ successful escape?',
        options: [
          {
            id: '0',
            text: 'The workers followed the drilling bore upward before the mine flooded.',
          },
          {
            id: '1',
            text: 'Early warning and an orderly, trained evacuation through the elevator.',
          },
          {
            id: '2',
            text: 'The workers stayed in sealed chambers until water levels returned to normal.',
          },
          {
            id: '3',
            text: 'The usual mine pumps kept the escape routes dry throughout the event.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Drilling disaster describes the electrician’s alarm and orderly evacuation.',
        sectionIds: ['escape'],
        evidence:
          'Drilling disaster describes the electrician’s alarm and orderly evacuation. Verified in assigned source section: Drilling_disaster.',
        sourceSection: {
          title: 'Wikipedia: Drilling disaster',
          url: 'https://en.wikipedia.org/wiki/Lake_Peigneur#Drilling_disaster',
        },
      },
      {
        id: 'b6-peigneur-5',
        prompt: 'Which uncertainty remained in the investigation described?',
        options: [
          {
            id: '0',
            text: 'Whether dissolving salt or a volcanic vent had created the mine’s caverns',
          },
          {
            id: '1',
            text: 'Whether reversing canal flow started the drilling breach or followed it',
          },
          {
            id: '2',
            text: 'Whether the drilling was misplaced or the mine maps were inaccurate',
          },
          {
            id: '3',
            text: 'Whether the miners’ evacuation occurred before or after the lake was refilled',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Cause states that lost evidence prevented assigning blame between drilling location and mapping accuracy.',
        sectionIds: ['evidence'],
        evidence:
          'Cause states that lost evidence prevented assigning blame between drilling location and mapping accuracy. Verified in assigned source section: Cause.',
        sourceSection: {
          title: 'Wikipedia: Cause',
          url: 'https://en.wikipedia.org/wiki/Lake_Peigneur#Cause',
        },
      },
    ],
  },
  {
    id: 'reading-mpesa',
    category: 'reading',
    title: 'M-Pesa',
    topic: 'M-Pesa',
    difficulty: 'Challenging',
    article: {
      title: 'M-Pesa',
      url: 'https://en.wikipedia.org/wiki/M-Pesa',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'A mobile-money system grew from a microfinance pilot into everyday payments and remittances. Read how customer behavior, basic phones and a network of local agents shaped the service.',
      scope: {
        wordCount: 1107,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/M-Pesa',
            wordCount: 292,
            anchors: [''],
          },
          {
            title: 'History',
            url: 'https://en.wikipedia.org/wiki/M-Pesa#History',
            wordCount: 645,
            anchors: ['History', 'Concept'],
          },
          {
            title: 'Services',
            url: 'https://en.wikipedia.org/wiki/M-Pesa#Services',
            wordCount: 170,
            anchors: ['Services'],
          },
        ],
      },
    },
    reading: {
      title: 'M-Pesa — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'agents',
          title: 'A digital balance still needs a cash interface',
          blocks: [
            {
              kind: 'paragraph',
              text: 'M-Pesa links mobile accounts with a network of local agents, including retailers and airtime sellers. Agents allow customers to exchange cash for a balance and convert a balance back into cash. The phone handles communication and account access, while the agent network connects the digital service to the physical cash economy.',
            },
            {
              kind: 'paragraph',
              text: 'This helps explain why the article treats agent trust and training as early obstacles. A working mobile interface alone is not the entire service. Customers also need usable places to enter and leave the system, and agents need to be willing and able to conduct those transactions.',
            },
          ],
        },
        {
          id: 'pivot',
          title: 'The pilot tested use, not only software',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The original concept focused on receiving and repaying microfinance loans. During the pilot, users found other uses, and the eventual service emphasized sending money home and making payments. Observing actual use changed the service’s value proposition rather than merely confirming a fixed design.',
            },
            {
              kind: 'paragraph',
              text: 'The article also mentions airtime purchases as a feature associated with increased transaction volume. The quiz does not ask for a historical promotional rate, because that number is incidental. The important lesson is the distinction between the planners’ initial task and the broader transactions customers wanted to perform.',
            },
          ],
        },
        {
          id: 'access',
          title: 'Reach depends on the infrastructure people already use',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The assigned history contrasts limited access to traditional banking with substantial mobile-phone use. Branch geography and costs made conventional services difficult for many intended users, while mobile infrastructure provided another channel. That combination helped make a phone-based service plausible in its launch setting.',
            },
            {
              kind: 'paragraph',
              text: 'The explanation is contextual, not a claim that bank branches ceased to exist or that every country would adopt the same service equally. The introduction reports different outcomes across markets. Technology adoption depends on the surrounding institutions and practical alternatives, not solely on whether software is technically available.',
            },
          ],
        },
        {
          id: 'roles',
          title: 'Operator and banking partner are different roles',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The services section identifies mobile network operators as running the platform and describes partnerships with banks for expanded services. A mobile-money interface can be connected to banking products without the telecom operator and a deposit-taking bank becoming the same institution.',
            },
            {
              kind: 'paragraph',
              text: 'This is an account of the roles described in the article, not advice about current legal protections or which product to use. It prevents the inaccurate shortcut that every service involving money must have the same institutional structure. The quiz avoids mutable fees, country-specific rules and investment decisions.',
            },
          ],
        },
        {
          id: 'interface',
          title: 'A smartphone app is not the only mobile interface',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article describes SIM-toolkit menus and USSD as ways customers access services. These interfaces use mobile-network capabilities and do not require the interaction model of a modern smartphone app. Different operators can present different menus while participating in a related service platform.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction helps explain the historical reach of mobile money: a phone can support account interactions without being a full desktop computer in miniature. It does not imply that every phone supports every feature or that later apps are absent. The question concerns the interfaces explicitly described in the assigned section.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'M-Pesa · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/M-Pesa',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b6-mpesa-1',
        prompt: 'What function does the local agent network perform?',
        options: [
          {
            id: '0',
            text: 'They supply the mobile radio network used to route messages.',
          },
          {
            id: '1',
            text: 'It provides physical cash-in and cash-out access to mobile balances.',
          },
          {
            id: '2',
            text: 'They decide each transfer’s destination when the customer’s phone is offline.',
          },
          {
            id: '3',
            text: 'They mainly underwrite the microfinance loans rather than exchange cash and balances.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Introduction and Services describe deposits and withdrawals through retail and airtime agents.',
        sectionIds: ['agents'],
        evidence:
          'Introduction and Services describe deposits and withdrawals through retail and airtime agents. Verified in assigned source section: Services.',
        sourceSection: {
          title: 'Wikipedia: Services',
          url: 'https://en.wikipedia.org/wiki/M-Pesa#Services',
        },
      },
      {
        id: 'b6-mpesa-2',
        prompt: 'How did the service’s focus change after the pilot?',
        options: [
          {
            id: '0',
            text: 'From remittances toward an exclusive focus on microfinance loan repayment',
          },
          {
            id: '1',
            text: 'From domestic transfers toward a service restricted to international remittances',
          },
          {
            id: '2',
            text: 'From customer-to-customer transfers toward lending directly from agents’ own funds',
          },
          {
            id: '3',
            text: 'From microfinance loan transactions toward remittances and payments',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Concept describes the initial loan focus and later remittance/payment value proposition.',
        sectionIds: ['pivot'],
        evidence:
          'Concept describes the initial loan focus and later remittance/payment value proposition. Verified in assigned source section: Concept.',
        sourceSection: {
          title: 'Wikipedia: Concept',
          url: 'https://en.wikipedia.org/wiki/M-Pesa#Concept',
        },
      },
      {
        id: 'b6-mpesa-3',
        prompt: 'Which combination made mobile delivery promising in the launch context described?',
        options: [
          {
            id: '0',
            text: 'Limited conventional banking access alongside widespread mobile use',
          },
          {
            id: '1',
            text: 'High bank-account ownership but too few conventional payment cards',
          },
          {
            id: '2',
            text: 'Widespread branch access but a shortage of international wire-transfer services',
          },
          {
            id: '3',
            text: 'Low mobile use offset by agents supplying customers with dedicated payment terminals',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'History contrasts geographic/cost barriers to banking with mobile penetration.',
        sectionIds: ['access'],
        evidence:
          'History contrasts geographic/cost barriers to banking with mobile penetration. Verified in assigned source section: History.',
        sourceSection: {
          title: 'Wikipedia: History',
          url: 'https://en.wikipedia.org/wiki/M-Pesa#History',
        },
      },
      {
        id: 'b6-mpesa-4',
        prompt: 'How does the article distinguish the platform operator from banks?',
        options: [
          {
            id: '0',
            text: 'Banks operate the core platform while telecom firms supply only advertising.',
          },
          {
            id: '1',
            text: 'Retail agents individually maintain and settle the entire transfer network.',
          },
          {
            id: '2',
            text: 'Mobile network operators run the service, with bank partnerships supporting additional products.',
          },
          {
            id: '3',
            text: 'The operator’s account balance is described as identical to every partnered bank product.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Services distinguishes network operators and bank partnerships.',
        sectionIds: ['roles'],
        evidence:
          'Services distinguishes network operators and bank partnerships. Verified in assigned source section: Services.',
        sourceSection: {
          title: 'Wikipedia: Services',
          url: 'https://en.wikipedia.org/wiki/M-Pesa#Services',
        },
      },
      {
        id: 'b6-mpesa-5',
        prompt: 'What do SIM-toolkit and USSD interfaces illustrate in the account?',
        options: [
          {
            id: '0',
            text: 'That mobile account access could use network-based menus',
          },
          {
            id: '1',
            text: 'That account access initially required installing a browser-based banking application',
          },
          {
            id: '2',
            text: 'That the service used only stored-value cards read by dedicated merchant terminals',
          },
          {
            id: '3',
            text: 'That account access was limited to calls in which an agent entered every instruction',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The Services section describes SIM toolkit and USSD menus, allowing the basic service to work without requiring a smartphone app.',
        sectionIds: ['interface'],
        evidence:
          'Services names STK and USSD menus. Verified in assigned source section: Services.',
        sourceSection: {
          title: 'Wikipedia: Services',
          url: 'https://en.wikipedia.org/wiki/M-Pesa#Services',
        },
      },
    ],
  },
  {
    id: 'reading-mechanical-turk',
    category: 'reading',
    title: 'Mechanical Turk',
    topic: 'Mechanical Turk',
    difficulty: 'Challenging',
    article: {
      title: 'Mechanical Turk',
      url: 'https://en.wikipedia.org/wiki/Mechanical_Turk',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'A celebrated chess-playing automaton concealed a skilled human operator. The opening sections explain how controlled inspections, hidden communication and mechanical linkages sustained the illusion.',
      scope: {
        wordCount: 1519,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Mechanical_Turk',
            wordCount: 223,
            anchors: [''],
          },
          {
            title: 'Construction',
            url: 'https://en.wikipedia.org/wiki/Mechanical_Turk#Construction',
            wordCount: 852,
            anchors: ['Construction'],
          },
          {
            title: 'Exhibition',
            url: 'https://en.wikipedia.org/wiki/Mechanical_Turk#Exhibition',
            wordCount: 444,
            anchors: ['Exhibition'],
          },
        ],
      },
    },
    reading: {
      title: 'Mechanical Turk — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'inspection',
          title: 'Seeing every compartment is not seeing all of them at once',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The cabinet was shown through a sequence of opened doors. Its internal layout and sliding seat allowed the hidden operator to move out of whichever area was being displayed. Visible clockwork also occupied less space than spectators might assume. The inspection sequence created confidence without exposing the entire interior simultaneously.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction matters because a demonstration can answer a narrower question than the audience thinks it answers. Seeing an apparently empty region at one moment does not prove that no person occupies a different region or can move into it later. The article explains the staging rather than asking readers to accept the display as a complete audit.',
            },
          ],
        },
        {
          id: 'sensing',
          title: 'The operator needed a view of the external game',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Magnets associated with the pieces affected indicators beneath the thin chessboard. Those indicators let the operator track moves on the board above, even while hidden inside. This supplied information about the game; it was not the source of the chess strategy itself. Observing an opponent’s move and choosing a reply were separate tasks, supported by different parts of the apparatus.',
            },
            {
              kind: 'paragraph',
              text: 'The operator’s own internal board and numbered positions helped translate the external state into a usable representation. Separating observation from decision-making clarifies the system: the mechanical components communicated positions, while a human chess player selected the response.',
            },
          ],
        },
        {
          id: 'action',
          title: 'A linkage transmitted a chosen move',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The operator used an internal mechanism linked to the figure’s arm, described as resembling a pantograph. Moving the control positioned the arm, and a lever operated the hand so it could grasp pieces. The visible action was mechanical even though the choice of action was human.',
            },
            {
              kind: 'paragraph',
              text: 'This is why calling the whole apparatus either “just a person” or “an autonomous chess machine” misses part of the explanation. Considerable engineering made the performance possible, but it did not establish automated chess reasoning. The question distinguishes a mechanism for moving pieces from a mechanism for deciding which move to make.',
            },
          ],
        },
        {
          id: 'tour',
          title: 'A closed solution can start at different places',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The knight’s-tour exhibition benefited from a prearranged closed-loop solution. In a closed tour, the last square connects by a legal knight move back to the first. Choosing a different square as the starting point can rotate the same cycle rather than require discovering a fresh route from scratch.',
            },
            {
              kind: 'paragraph',
              text: 'This explains how a flexible-looking demonstration can rely on a stored structure. It does not show that every arbitrary sequence of knight moves would work, or that a legal move always reaches an unvisited square. The special property is a full closed cycle covering the board once.',
            },
          ],
        },
        {
          id: 'communication',
          title: 'The performance had a hidden communication channel',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article describes paired numbered discs, one inside and one outside, connected by a rod. They supplied a code between the concealed operator and the presenter. That channel allowed coordination without announcing the human arrangement to spectators.',
            },
            {
              kind: 'paragraph',
              text: 'The presenter walking around the room therefore did not establish that the machine operated independently. The apparatus had ways to conceal both the operator and communication. The broader inference is specific: an impressive output and a convincing staged demonstration do not, by themselves, identify the process generating the output.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Mechanical Turk · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Mechanical_Turk',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b6-mechanical-turk-1',
        prompt: 'Why did opening cabinet doors fail to reveal the hidden operator?',
        options: [
          {
            id: '0',
            text: 'Mirrors produced the illusion of an empty cabinet while the operator stayed in one fixed seat.',
          },
          {
            id: '1',
            text: 'The operator remained outside, controlling the machine entirely through the floor.',
          },
          {
            id: '2',
            text: 'The displayed cabinet was exchanged for a second cabinet before each game.',
          },
          {
            id: '3',
            text: 'The operator could shift position while compartments were displayed in sequence.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Construction describes the sliding seat, shallow dummy machinery and staged openings.',
        sectionIds: ['inspection'],
        evidence:
          'Construction describes the sliding seat, shallow dummy machinery and staged openings. Verified in assigned source section: Construction.',
        sourceSection: {
          title: 'Wikipedia: Construction',
          url: 'https://en.wikipedia.org/wiki/Mechanical_Turk#Construction',
        },
      },
      {
        id: 'b6-mechanical-turk-2',
        prompt: 'How could the hidden player track moves made on the external board?',
        options: [
          {
            id: '0',
            text: 'Through a concealed sightline from the operator’s seat directly onto the board',
          },
          {
            id: '1',
            text: 'Through magnetic indicators below the chessboard',
          },
          {
            id: '2',
            text: 'Through the presenter announcing each move using the numbered communication discs',
          },
          {
            id: '3',
            text: 'Through pressure switches under squares operating a clockwork recording sheet',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Construction describes magnets beneath pieces and indicators beneath the board.',
        sectionIds: ['sensing'],
        evidence:
          'Construction describes magnets beneath pieces and indicators beneath the board. Verified in assigned source section: Construction.',
        sourceSection: {
          title: 'Wikipedia: Construction',
          url: 'https://en.wikipedia.org/wiki/Mechanical_Turk#Construction',
        },
      },
      {
        id: 'b6-mechanical-turk-3',
        prompt: 'What role did the internal linkage to the arm serve?',
        options: [
          {
            id: '0',
            text: 'It translated magnetic board readings directly into a precomputed reply.',
          },
          {
            id: '1',
            text: 'It primarily synchronized the numbered discs used by the presenter.',
          },
          {
            id: '2',
            text: 'It translated the operator’s controls into visible piece movement.',
          },
          {
            id: '3',
            text: 'It let the external opponent move pieces without touching the public board.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'The pantograph-like mechanism controlled arm and hand motion.',
        sectionIds: ['action'],
        evidence:
          'The pantograph-like mechanism controlled arm and hand motion. Verified in assigned source section: Construction.',
        sourceSection: {
          title: 'Wikipedia: Construction',
          url: 'https://en.wikipedia.org/wiki/Mechanical_Turk#Construction',
        },
      },
      {
        id: 'b6-mechanical-turk-4',
        prompt:
          'Why was a closed knight’s tour useful for demonstrations from different starting squares?',
        options: [
          {
            id: '0',
            text: 'The same cycle could be followed beginning at different points.',
          },
          {
            id: '1',
            text: 'The endpoints of an open tour could always be relabeled to match any requested square.',
          },
          {
            id: '2',
            text: 'Any legal knight move necessarily leaves an equally solvable smaller board.',
          },
          {
            id: '3',
            text: 'The demonstration changed the knight’s move rule when the chosen start required it.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Exhibition attributes the tour to a marked reentrant solution; rotation of a closed cycle is the stated inference.',
        sectionIds: ['tour'],
        evidence:
          'Exhibition attributes the tour to a marked reentrant solution; rotation of a closed cycle is the stated inference. Verified in assigned source section: Exhibition.',
        sourceSection: {
          title: 'Wikipedia: Exhibition',
          url: 'https://en.wikipedia.org/wiki/Mechanical_Turk#Exhibition',
        },
      },
      {
        id: 'b6-mechanical-turk-5',
        prompt: 'What was the purpose of the paired numbered discs?',
        options: [
          {
            id: '0',
            text: 'Indexing the board squares so the operator could sense external piece positions',
          },
          {
            id: '1',
            text: 'Coded communication between presenter and hidden operator',
          },
          {
            id: '2',
            text: 'Measuring elapsed thinking time separately for the two players',
          },
          {
            id: '3',
            text: 'Selecting a starting point for the knight’s tour without the presenter’s input',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Construction describes the inside/outside discs as a code between operator and presenter.',
        sectionIds: ['communication'],
        evidence:
          'Construction describes the inside/outside discs as a code between operator and presenter. Verified in assigned source section: Construction.',
        sourceSection: {
          title: 'Wikipedia: Construction',
          url: 'https://en.wikipedia.org/wiki/Mechanical_Turk#Construction',
        },
      },
    ],
  },
  {
    id: 'reading-timebank',
    category: 'reading',
    title: 'Time-based currency',
    topic: 'Timebanking',
    difficulty: 'Challenging',
    article: {
      title: 'Time-based currency',
      url: 'https://en.wikipedia.org/wiki/Time-based_currency',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'Some service exchanges use hours rather than money as their accounting unit. Read how credits circulate through a community, what equal valuation is meant to recognize, and the practical constraints that remain.',
      scope: {
        wordCount: 1201,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Time-based_currency',
            wordCount: 166,
            anchors: [''],
          },
          {
            title: 'Time dollars',
            url: 'https://en.wikipedia.org/wiki/Time-based_currency#Time_dollars',
            wordCount: 492,
            anchors: ['Time_dollars', 'Timebanks', 'Timebanking'],
          },
          {
            title: 'Timebanking and the timebank',
            url: 'https://en.wikipedia.org/wiki/Time-based_currency#Timebanking_and_the_timebank',
            wordCount: 355,
            anchors: ['Timebanking_and_the_timebank', 'Time_credit'],
          },
          {
            title: 'Criticisms',
            url: 'https://en.wikipedia.org/wiki/Time-based_currency#Criticisms',
            wordCount: 188,
            anchors: ['Criticisms'],
          },
        ],
      },
    },
    reading: {
      title: 'Timebanking — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'unit',
          title: 'Equal accounting value is a design choice',
          blocks: [
            {
              kind: 'paragraph',
              text: 'In the traditional equal-hour system described, an hour of one person’s service earns the same credit as an hour of another’s, regardless of the market wage for those tasks. This rule deliberately recognizes contributions on a different basis from ordinary market pricing.',
            },
            {
              kind: 'paragraph',
              text: 'The rule is about the unit used in the exchange, not proof that every skill takes equal training or that all services are equally available. The article distinguishes time-based systems broadly from particular timebanking conventions. The quiz specifies the traditional one-hour-one-credit arrangement when relying on that rule.',
            },
          ],
        },
        {
          id: 'network',
          title: 'Reciprocity can run through several people',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A participant earns credits by helping someone and can use them to receive help from another participant. The return service need not come from the original recipient. Recording credits and debits lets reciprocal exchange operate across a network rather than requiring two people to want exactly what each other can offer at the same moment.',
            },
            {
              kind: 'paragraph',
              text: 'This differs from a direct barter in which each side must arrange the matching return service with the same partner. It also differs from unrecorded volunteering, because the system provides an explicit account of earned and spent service credits. The value of the arrangement depends on people being able to find services they actually need.',
            },
          ],
        },
        {
          id: 'coordination',
          title: 'An accounting unit does not organize itself',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Timebanks need ways to record exchanges and connect requests with people able to help. The article describes both member-managed arrangements and paid coordinators, with specialized software sometimes assisting. The system can be simple, but the coordination work remains a real task.',
            },
            {
              kind: 'paragraph',
              text: 'The fact that services are exchanged without conventional payment does not imply that running the organization has no monetary cost. Staff, administration and continuity can require funding. That difference between exchange accounting and organizational resources helps explain the sustainability concerns described in the criticism section.',
            },
          ],
        },
        {
          id: 'availability',
          title: 'Credits do not create missing skills',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A positive credit balance gives a participant a claim within the exchange’s rules, but cannot guarantee that a needed service is offered by someone available. The article specifically raises shortages of particular skills. A ledger can record demand without ensuring that matching supply exists.',
            },
            {
              kind: 'paragraph',
              text: 'The same limitation matters when comparing a theoretical benefit with an actual local program. A broad menu on paper is different from a reliable provider at the required time. The quiz therefore treats service availability and organizational sustainability as constraints, rather than assuming the accounting rule makes every need satisfiable.',
            },
          ],
        },
        {
          id: 'saving',
          title: 'The unit stays an hour, but practical access can still vary',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The described time credit can be saved without earning interest. Its nominal unit remains an hour of service rather than a claim that automatically grows with time. That design distinguishes its accounting from an interest-bearing money account.',
            },
            {
              kind: 'paragraph',
              text: 'However, a fixed time unit is not a guarantee about the range, quality or availability of services in the future. The article’s criticism about shortages remains relevant even if a ledger continues to show the same number of hours. The explanation avoids turning claims about resisting inflation into a universal promise about real purchasing power.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Timebanking · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Time-based_currency',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b6-timebank-1',
        prompt:
          'Under the traditional equal-hour rule, how are one hour of tutoring and one hour of gardening credited?',
        options: [
          {
            id: '0',
            text: 'Each earns one time credit.',
          },
          {
            id: '1',
            text: 'Each earns credits proportional to the usual local hourly wage.',
          },
          {
            id: '2',
            text: 'Each earns credits set by how scarce that service is in the network.',
          },
          {
            id: '3',
            text: 'Each earns credits agreed in a separate bilateral bargain, with no common hourly rule.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Time dollars and Time credit state the equal-hour rule regardless of skill or service.',
        sectionIds: ['unit'],
        evidence:
          'Time dollars and Time credit state the equal-hour rule regardless of skill or service. Verified in assigned source section: Time_credit.',
        sourceSection: {
          title: 'Wikipedia: Time credit',
          url: 'https://en.wikipedia.org/wiki/Time-based_currency#Time_credit',
        },
      },
      {
        id: 'b6-timebank-2',
        prompt: 'Why need the return service not come from the same person originally helped?',
        options: [
          {
            id: '0',
            text: 'The coordinator personally provides every service owed by someone else.',
          },
          {
            id: '1',
            text: 'A credit can only be redeemed by the same two people, but at a later date.',
          },
          {
            id: '2',
            text: 'Recorded credits can be spent with other participants in the network.',
          },
          {
            id: '3',
            text: 'Members exchange services without recording which participant earned or spent credit.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Introduction and Timebanking and the timebank describe earning from helping members and spending on services from others.',
        sectionIds: ['network'],
        evidence:
          'Introduction and Timebanking and the timebank describe earning from helping members and spending on services from others. Verified in assigned source section: Timebanking_and_the_timebank.',
        sourceSection: {
          title: 'Wikipedia: Timebanking and the timebank',
          url: 'https://en.wikipedia.org/wiki/Time-based_currency#Timebanking_and_the_timebank',
        },
      },
      {
        id: 'b6-timebank-3',
        prompt: 'Which task may a timebank coordinator perform?',
        options: [
          {
            id: '0',
            text: 'Convert each hour into a cash wage using a central price list',
          },
          {
            id: '1',
            text: 'Record exchanges and match service requests with providers',
          },
          {
            id: '2',
            text: 'Supply the missing skilled labor whenever a member requests an unavailable service',
          },
          {
            id: '3',
            text: 'Set the exchange rate between tutoring hours and gardening hours under the equal-hour rule',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Timebanking and the timebank explicitly describes transaction tracking and matching.',
        sectionIds: ['coordination'],
        evidence:
          'Timebanking and the timebank explicitly describes transaction tracking and matching. Verified in assigned source section: Timebanking_and_the_timebank.',
        sourceSection: {
          title: 'Wikipedia: Timebanking and the timebank',
          url: 'https://en.wikipedia.org/wiki/Time-based_currency#Timebanking_and_the_timebank',
        },
      },
      {
        id: 'b6-timebank-4',
        prompt: 'Why might a member with credits still be unable to obtain a desired service?',
        options: [
          {
            id: '0',
            text: 'A credit is valid only for the exact kind of task originally performed.',
          },
          {
            id: '1',
            text: 'Every saved hour loses purchasing units automatically as ordinary wages rise.',
          },
          {
            id: '2',
            text: 'Credits can only be redeemed with the original recipient of the work.',
          },
          {
            id: '3',
            text: 'The community may lack someone offering the required skill.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Criticisms notes that the supply of particular skills may be lacking.',
        sectionIds: ['availability'],
        evidence:
          'Criticisms notes that the supply of particular skills may be lacking. Verified in assigned source section: Criticisms.',
        sourceSection: {
          title: 'Wikipedia: Criticisms',
          url: 'https://en.wikipedia.org/wiki/Time-based_currency#Criticisms',
        },
      },
      {
        id: 'b6-timebank-5',
        prompt:
          'Which statement about stored credits matches the described design while respecting its limits?',
        options: [
          {
            id: '0',
            text: 'They compound automatically, so an hour saved becomes more hours later.',
          },
          {
            id: '1',
            text: 'They fix both the number of hours and the availability of a chosen future provider.',
          },
          {
            id: '2',
            text: 'They retain an hourly accounting unit, but availability of services is not guaranteed.',
          },
          {
            id: '3',
            text: 'They are periodically repriced against the market wage of the original service.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Time credit specifies no interest and a fixed hour; Criticisms qualifies actual availability.',
        sectionIds: ['saving'],
        evidence:
          'Time credit specifies no interest and a fixed hour; Criticisms qualifies actual availability. Verified in assigned source section: Time_credit.',
        sourceSection: {
          title: 'Wikipedia: Time credit',
          url: 'https://en.wikipedia.org/wiki/Time-based_currency#Time_credit',
        },
      },
    ],
  },
  {
    id: 'reading-moon-hoax',
    category: 'reading',
    title: 'Great Moon Hoax',
    topic: 'Great Moon Hoax',
    difficulty: 'Challenging',
    article: {
      title: 'Great Moon Hoax',
      url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'A newspaper presented invented lunar discoveries under the authority of a real astronomer. The article connects the story’s scientific disguise with satire, reader expectations and the economics of the penny press.',
      scope: {
        wordCount: 1036,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax',
            wordCount: 138,
            anchors: [''],
          },
          {
            title: 'Hoax',
            url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax#Hoax',
            wordCount: 170,
            anchors: ['Hoax'],
          },
          {
            title: 'Authorship',
            url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax#Authorship',
            wordCount: 287,
            anchors: ['Authorship'],
          },
          {
            title: 'Reactions',
            url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax#Reactions',
            wordCount: 269,
            anchors: ['Reactions'],
          },
          {
            title: 'Context',
            url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax#Context',
            wordCount: 172,
            anchors: ['Context'],
          },
        ],
      },
    },
    reading: {
      title: 'Great Moon Hoax — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'authority',
          title: 'A real name can lend credibility to a false claim',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The reports attributed extraordinary lunar observations to John Herschel, a real and respected astronomer. The supposed companion writing the account, Andrew Grant, was fictional. Combining a recognizable authority with an invented narrator made the story appear connected to genuine scientific work without that connection being real.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction is between a named authority and verified authorship or evidence. A report can borrow a scientist’s reputation without the scientist having made its claims. The question asks about that mechanism, rather than treating the existence of Herschel as confirmation of the observations.',
            },
          ],
        },
        {
          id: 'instrument',
          title: 'Technical detail can make a story persuasive without making it true',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The story claimed an enormous telescope using a new principle had revealed the lunar world. It later explained the end of observations by claiming sunlight had damaged the observatory through the instrument. Those details supplied both an apparent means of discovery and a narrative reason no further observations followed.',
            },
            {
              kind: 'paragraph',
              text: 'The source presents these as components of the fabricated account, not actual astronomical findings. Technical-sounding specifics can make a story vivid while leaving its central evidence unverified. Remembering the role of the instrument is more useful here than memorizing the fictional lens’s dimensions.',
            },
          ],
        },
        {
          id: 'motives',
          title: 'A commercial incentive and satire can coexist',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The authorship section attributes the work to Richard Adams Locke and describes likely motives including sales and ridicule of extravagant contemporary astronomical speculation. A story can have more than one intended function. Treating satire and commercial sensationalism as mutually exclusive would oversimplify the account.',
            },
            {
              kind: 'paragraph',
              text: 'The article qualifies intentions as probable and discusses authorship rumors rather than presenting every detail of motive as directly observable fact. The quiz preserves that distinction: it asks what combination the account discusses, not whether a later reader can know every private intention with certainty.',
            },
          ],
        },
        {
          id: 'business',
          title: 'Reader attention had economic value',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The penny press depended substantially on advertising, which benefited from large readerships. Sensational stories could attract attention and expose more people to advertisements. This supplies a business context for publishing an extraordinary claim, although an incentive is not itself proof of a particular sales effect.',
            },
            {
              kind: 'paragraph',
              text: 'The article also cautions that popular accounts exaggerated how much the hoax boosted circulation. Both points can hold: the newspaper had reasons to seek readers, and a dramatic numerical success story may be unreliable. Explaining incentives should not silently turn into asserting an unverified outcome.',
            },
          ],
        },
        {
          id: 'response',
          title: 'The borrowed authority did not control the story',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Herschel initially found the account amusing but later became irritated by questions from people who believed it. His reaction illustrates the consequences of a reputation being used in a story someone else produced. He was not described as the discoverer later retracting genuine observations.',
            },
            {
              kind: 'paragraph',
              text: 'The reading also separates acknowledgment of fabrication from a formal retraction and discusses uncertain circulation legends. Those distinctions matter in media history: repetition can preserve a simplified success narrative even after the core story is known to be false. The quiz emphasizes the explicit qualification rather than asking for a changing interpretation of a publication date.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Great Moon Hoax · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b6-moon-hoax-1',
        prompt: 'How did the reports borrow scientific authority?',
        options: [
          {
            id: '0',
            text: 'It cited a real lunar expedition led by Herschel but embellished the expedition’s findings.',
          },
          {
            id: '1',
            text: 'It published a statement Herschel had signed before independently testing his observations.',
          },
          {
            id: '2',
            text: 'They falsely attributed discoveries to the real astronomer John Herschel.',
          },
          {
            id: '3',
            text: 'It relied on independent observatories reproducing the same images for verification.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Introduction and Context distinguish the real astronomer from the false attribution.',
        sectionIds: ['authority'],
        evidence:
          'Introduction and Context distinguish the real astronomer from the false attribution. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax',
        },
      },
      {
        id: 'b6-moon-hoax-2',
        prompt:
          'What role did the claimed destruction of the telescope play in the fabricated narrative?',
        options: [
          {
            id: '0',
            text: 'A change in the Moon’s orbit made it inaccessible to southern observatories.',
          },
          {
            id: '1',
            text: 'It explained why the supposed observations stopped.',
          },
          {
            id: '2',
            text: 'Herschel withdrew the telescope after peer reviewers rejected his interpretation.',
          },
          {
            id: '3',
            text: 'The expedition exhausted its photographic plates and could no longer preserve images.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Hoax says observations were supposedly terminated by destruction of the telescope.',
        sectionIds: ['instrument'],
        evidence:
          'Hoax says observations were supposedly terminated by destruction of the telescope. Verified in assigned source section: Hoax.',
        sourceSection: {
          title: 'Wikipedia: Hoax',
          url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax#Hoax',
        },
      },
      {
        id: 'b6-moon-hoax-3',
        prompt: 'Which pair of likely motives is discussed for Locke?',
        options: [
          {
            id: '0',
            text: 'Defending literal astronomical speculation while discouraging commercial circulation',
          },
          {
            id: '1',
            text: 'Reporting a scientific discovery before the slower scholarly journals could print it',
          },
          {
            id: '2',
            text: 'Creating a private joke that was never intended for newspaper publication',
          },
          {
            id: '3',
            text: 'Increasing sales and satirizing extravagant astronomical speculation',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Authorship discusses sensational sales appeal and satire as probable motives.',
        sectionIds: ['motives'],
        evidence:
          'Authorship discusses sensational sales appeal and satire as probable motives. Verified in assigned source section: Authorship.',
        sourceSection: {
          title: 'Wikipedia: Authorship',
          url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax#Authorship',
        },
      },
      {
        id: 'b6-moon-hoax-4',
        prompt: 'How did the penny-press business context reward attention?',
        options: [
          {
            id: '0',
            text: 'Large readerships helped sustain advertising income.',
          },
          {
            id: '1',
            text: 'Advertising revenue was fixed independently of readership, so extra readers only added cost.',
          },
          {
            id: '2',
            text: 'The main incentive was a government payment for each astronomical report published.',
          },
          {
            id: '3',
            text: 'The business relied chiefly on expensive subscriptions from a small specialist audience.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Context explicitly connects advertising revenue and reader numbers.',
        sectionIds: ['business'],
        evidence:
          'Context explicitly connects advertising revenue and reader numbers. Verified in assigned source section: Context.',
        sourceSection: {
          title: 'Wikipedia: Context',
          url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax#Context',
        },
      },
      {
        id: 'b6-moon-hoax-5',
        prompt: 'What caution does the article give about the hoax’s effect on circulation?',
        options: [
          {
            id: '0',
            text: 'The paper’s own claimed increase is treated as a precise independently verified count.',
          },
          {
            id: '1',
            text: 'Popular accounts have exaggerated the size of the increase.',
          },
          {
            id: '2',
            text: 'The account distinguishes fiction from fact but accepts every later sales legend unchanged.',
          },
          {
            id: '3',
            text: 'Historians agree that the hoax permanently reduced circulation below its earlier level.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Reactions explicitly warns that circulation gains have been exaggerated.',
        sectionIds: ['response'],
        evidence:
          'Reactions explicitly warns that circulation gains have been exaggerated. Verified in assigned source section: Reactions.',
        sourceSection: {
          title: 'Wikipedia: Reactions',
          url: 'https://en.wikipedia.org/wiki/Great_Moon_Hoax#Reactions',
        },
      },
    ],
  },
];
